import { useCallback, useMemo } from 'react';
import { useDisclosure } from '@withjoy/joykit';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useEventInfo } from '@shared/utils/eventInfo';
import { withWindow } from '@shared/utils/withWindow';
import { stripUrlProtocol } from '@shared/utils/strings';
import { getLocalStorage } from '@shared/core/storage';
import { usePreloadTranslationNamespaces } from '@shared/hooks/usePreloadTranslationNamespaces';
import { useCurrentStationerySale } from './useCurrentStationerySale';
import type { StationeryCrossSellDialogProps } from './StationeryCrossSellDialog.types';

const SEEN_STORAGE_KEY_PREFIX = 'stationeryCrossSell:seen:';

/**
 * Reusable controller for the publish-time stationery cross-sell modal — mirrors
 * the `useEmailVerificationDialog` pattern. Wire it into any "publish site"
 * success handler (admin or guest):
 *
 * 1. Render the (lazy) dialog inline, gated on `dialogProps.isOpen`:
 *    `{dialogProps.isOpen && <LazyStationeryCrossSellDialog {...dialogProps} />}`
 * 2. On successful publish call `handlePublishSuccess()`.
 *
 * `handlePublishSuccess` returns `true` when it opened the modal — the caller
 * should then skip its own success toast — and `false` otherwise (feature flag
 * off, or already shown once for this event), in which case the caller shows its
 * normal feedback. Shown at most once per event; the "seen" flag is persisted in
 * localStorage keyed by eventId.
 */
export const usePublishCrossSell = () => {
  // Warm the modal's namespace at the publish site so the lazy dialog renders
  // its strings immediately on open instead of flickering in after load.
  usePreloadTranslationNamespaces('stationeryCrossSell');
  const [enabled] = useIsFeatureEnabled('printSiteCrossSellModal');
  const { eventInfo, eventHandle } = useEventInfo();
  const eventId = eventInfo?.eventId;
  const sale = useCurrentStationerySale();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const origin = withWindow(w => w.location.origin, '');
  const websiteUrl = eventHandle ? `${origin}/${eventHandle}` : '';
  const displayUrl = stripUrlProtocol(websiteUrl);

  const handlePublishSuccess = useCallback((): boolean => {
    // Only surface the modal when there's an active promo to advertise — otherwise
    // it would show "up to 40% off" + a Claim Discount CTA with no code. Falling
    // through here also avoids burning the once-per-event key before a real sale exists.
    if (!enabled || !eventId || !sale?.code) return false;
    const storage = getLocalStorage();
    const key = `${SEEN_STORAGE_KEY_PREFIX}${eventId}`;
    if (storage.getItem(key)) return false;
    storage.setItem(key, 'true');
    onOpen();
    return true;
  }, [enabled, eventId, sale, onOpen]);

  const dialogProps: StationeryCrossSellDialogProps = useMemo(() => ({ isOpen, onClose, websiteUrl, displayUrl }), [isOpen, onClose, websiteUrl, displayUrl]);

  return { handlePublishSuccess, dialogProps };
};
